import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../../catalyst/dialog';
import { Button } from '../../catalyst/button';
import { Input } from '../../catalyst/input';
import backend from '../../services/backend';

interface CategoryPickerDialogProps {
  open: boolean;
  onClose: () => void;
  /** null unsets the page's category entirely (e.g. a blank page). */
  onPick: (categoryName: string | null) => void;
  current: string | null;
  suggested: string | null;
  /** Pages the correction will land on: the reviewed page plus every selected similar page. */
  blastRadius: number;
  /**
   * False while the group cohort is (re)loading. The dialog stays open but every action
   * is locked: a cohort that changed under an open picker would land the correction on a
   * group the curator never saw (DR-3318).
   */
  membersSettled: boolean;
}

export function CategoryPickerDialog({
  open,
  onClose,
  onPick,
  current,
  suggested,
  blastRadius,
  membersSettled,
}: CategoryPickerDialogProps) {
  const categoriesQuery = useQuery({
    queryKey: ['categories'],
    queryFn: () => backend.listCategories().then((res) => res.data),
  });
  const [search, setSearch] = useState('');

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  const allCategories = useMemo(() => {
    const items = categoriesQuery.data?.items;
    if (!items) return [];
    const names = Array.from(new Set(items.map((c) => c.name))).filter((name) => name !== current);
    return names.sort((a, b) => a.localeCompare(b));
  }, [categoriesQuery.data, current]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allCategories;
    return allCategories.filter((c) => c.toLowerCase().includes(q));
  }, [allCategories, search]);

  // Suggested shown at top (if not already first in filtered)
  const displayList = useMemo(() => {
    if (!suggested) return filtered;
    const withoutSuggested = filtered.filter((c) => c !== suggested);
    const suggestedInFiltered = filtered.includes(suggested);
    if (!suggestedInFiltered) return filtered;
    return [suggested, ...withoutSuggested];
  }, [filtered, suggested]);

  const handlePick = (name: string) => {
    if (!membersSettled) return;
    onPick(name);
    handleClose();
  };

  const handleClear = () => {
    if (!membersSettled) return;
    onPick(null);
    handleClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filtered.length === 1) {
      handlePick(filtered[0]);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} size="md">
      <DialogTitle>Pick a category</DialogTitle>
      <DialogBody>
        {blastRadius > 1 && (
          <p className="mb-3 text-sm text-gray-500 dark:text-zinc-400">
            Applies to{' '}
            <span className="font-medium text-gray-700 dark:text-zinc-200">{blastRadius} pages</span>{' '}
            → the category you pick. Every selected page is set to it, whatever it is labelled now.
          </p>
        )}
        {!membersSettled && (
          <p className="mb-3 text-sm text-gray-500 dark:text-zinc-400">
            Checking which similar pages are still flagged — picking unlocks in a moment.
          </p>
        )}
        <div className="mb-3">
          <Input
            autoFocus
            placeholder="Search categories…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-zinc-700 divide-y divide-gray-100 dark:divide-zinc-700">
          {categoriesQuery.isLoading && (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-zinc-400">Loading…</div>
          )}
          {!categoriesQuery.isLoading && displayList.length === 0 && (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-zinc-400">No categories match.</div>
          )}
          {displayList.map((name) => {
            const isSuggested = name === suggested;
            return (
              <button
                key={name}
                type="button"
                disabled={!membersSettled}
                onClick={() => handlePick(name)}
                className="w-full text-left px-4 py-2.5 text-sm text-gray-900 dark:text-zinc-100 hover:bg-indigo-50 dark:hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent flex items-center justify-between gap-2 transition-colors"
              >
                <span>{name}</span>
                {isSuggested && (
                  <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 shrink-0">
                    suggested
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={handleClose}>
          Cancel
        </Button>
        <Button outline disabled={!membersSettled} onClick={handleClear}>
          No category (blank page)
          {blastRadius > 1 ? ` · ${blastRadius} pages → uncategorized` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
